import React, { useEffect, useRef, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, convertToRaw, EditorState, Modifier, SelectionState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import htmlToDraft from 'html-to-draftjs';

const MyEditor = ({
	memberList,
	styleList,
	setEditorContent,
	setMentionIds,
	defaultMention,
	showFullToolbar
}) => {
	let initialEditorState;
	if (defaultMention) {
		const contentBlock = htmlToDraft(defaultMention);
		if (contentBlock) {
			const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
			initialEditorState = EditorState.createWithContent(contentState);
		} else {
			initialEditorState = EditorState.createEmpty();
		}
	} else {
		initialEditorState = EditorState.createEmpty();
	}
	const [editorState, setEditorState] = useState(initialEditorState);
	const [suggestions, setSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [selectedStyleIndex, setSelectedStyleIndex] = useState(-1);
	const [styleSuggestions, setStyleSuggestions] = useState([]);
	const [originalStyleList, setOriginalStyleList] = useState([]);
	const [isStyleSelected, setIsStyleSelected] = useState(false);
	const [isInitialSelection, setIsInitialSelection] = useState(true);
	const editorRef = useRef(null);

	useEffect(() => {
		if (editorRef.current) {
			editorRef.current.focusEditor();
		}
	}, []);

	useEffect(() => {
		if (memberList) {
			let tmpData = memberList.map((item) => ({
				text: item.value,
				value: item.value,
				url: item.memberId
			}));
			setSuggestions(tmpData);
		}
	}, [memberList]);

	useEffect(() => {
		if (styleList) {
			setStyleSuggestions(styleList);
			setOriginalStyleList(styleList);
		}
	}, [styleList]);

	const onEditorStateChange = (editorState) => {
		setEditorState(editorState);
	};

	const handleKeyDown = (e) => {
		if (!showSuggestions || styleSuggestions.length === 0) return;

		const maxIndex = styleSuggestions.length - 1;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setIsInitialSelection(false);
				setSelectedStyleIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setIsInitialSelection(false);
				setSelectedStyleIndex((prev) => (prev > 0 ? prev - 1 : prev));
				break;
			case 'Enter':
				e.preventDefault();
				if (selectedStyleIndex >= 0) {
					insertStyleMention(styleSuggestions[selectedStyleIndex]);
					setShowSuggestions(false);
					setIsInitialSelection(true);
				}
				break;
			case 'Escape':
				setShowSuggestions(false);
				setSelectedStyleIndex(-1);
				setIsInitialSelection(true);
				break;
		}
	};

	const insertStyleMention = (style) => {
		const contentState = editorState.getCurrentContent();
		const selectionState = editorState.getSelection();
		const currentBlock = contentState.getBlockForKey(selectionState.getAnchorKey());
		const blockText = currentBlock.getText();
		const cursorPosition = selectionState.getAnchorOffset();

		let wordStart = cursorPosition;
		while (wordStart > 0 && blockText[wordStart - 1] !== ' ' && blockText[wordStart - 1] !== '#') {
			wordStart--;
		}
		if (blockText[wordStart - 1] === '#') {
			wordStart--;
		}

		const wordSelection = selectionState.merge({
			anchorOffset: wordStart,
			focusOffset: cursorPosition
		});

		const newContentState = Modifier.replaceText(contentState, wordSelection, `${style.text} `);
		const newSelectionState = SelectionState.createEmpty(
			newContentState.getSelectionBefore().getAnchorKey()
		).merge({
			anchorOffset: newContentState.getSelectionBefore().getAnchorOffset() + `${style.text} `.length,
			focusOffset: newContentState.getSelectionBefore().getAnchorOffset() + `${style.text} `.length
		});

		const newEditorState = EditorState.push(editorState, newContentState, 'insert-style-mention');
		const finalEditorState = EditorState.forceSelection(newEditorState, newSelectionState);

		setEditorState(finalEditorState);
		setShowSuggestions(false);
		setSelectedStyleIndex(-1);
		setIsStyleSelected(true);
		setStyleSuggestions(originalStyleList);

		setTimeout(() => {
			setIsStyleSelected(false);
		}, 100);

		setTimeout(() => {
			if (editorRef.current) {
				editorRef.current.focusEditor();
			}
		}, 0);
	};

	const renderStyleSuggestions = () => {
		if (showSuggestions) {
			return (
				<div className="rdw-suggestion-dropdown style-suggestion-dropdown" style={{ top: '50px' }}>
					{styleSuggestions.map((item, index) => (
						<div
							key={item.url}
							className={`rdw-suggestion-option d-flex align-items-center ${index === selectedStyleIndex ? 'selected' : ''}`}
							style={{
								padding: '8px 12px',
								cursor: 'pointer',
								backgroundColor: index === selectedStyleIndex ? '#e6f7ff' : 'transparent'
							}}
							onClick={() => insertStyleMention(item)}
						>
							<div
								className={`${item?.isSet ? 'set-image-layout' : 'design-image'}`}
								style={{ width: '40px' }}
							>
								<img src={item.designDocuments[0]?.docUrl} alt="image 1" />
								{item?.isSet && <img src={item.designDocuments[1]?.docUrl} alt="image 2" />}
							</div>

							<div className="ml-2">
								<p className="semibold-12">{item.text}</p>
								<p className="regular-12 gray_dark_03 mt-1">{item.name}</p>
							</div>
						</div>
					))}
				</div>
			);
		}
		return null;
	};

	useEffect(() => {
		if (showSuggestions) {
			document.addEventListener('keydown', handleKeyDown);
		}
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [showSuggestions, selectedStyleIndex, styleSuggestions]);

	useEffect(() => {
		const content = convertToRaw(editorState.getCurrentContent());
		const rawHtml = draftToHtml(content);

		setEditorContent(rawHtml);

		let ids = [];
		if (content?.entityMap) {
			for (let key in content.entityMap) {
				if (content.entityMap[key].type === 'MENTION') {
					ids.push(content.entityMap[key].data.url);
				}
			}
		}
		setMentionIds(ids);
	}, [editorState, styleSuggestions]);

	const handleContentStateChange = (contentState) => {
		let str = contentState.blocks[contentState.blocks.length - 1].text;
		const lastChar = str.charAt(str.length - 1);

		if (lastChar === '#' && !isStyleSelected) {
			setShowSuggestions(true);
			setSelectedStyleIndex(0);
			setIsInitialSelection(true);
			setStyleSuggestions(originalStyleList);
		} else if (showSuggestions && !isStyleSelected) {
			const currentWord = str.split(' ').pop();
			if (currentWord.startsWith('#')) {
				const searchText = currentWord.substring(1).toUpperCase();
				const filteredSuggestions = originalStyleList.filter(
					(item) => item.text && item.text.toUpperCase().includes(searchText)
				);
				setStyleSuggestions(filteredSuggestions);
				if (isInitialSelection) {
					setSelectedStyleIndex(filteredSuggestions.length > 0 ? 0 : -1);
				}
			} else {
				setShowSuggestions(false);
				setSelectedStyleIndex(-1);
				setIsInitialSelection(true);
			}
		}

		if (lastChar === '@' || lastChar === '#') {
			setTimeout(() => {
				const scrollContainer = document.querySelector('.rdw-suggestion-dropdown');
				if (!scrollContainer) return;

				let scrollIncrement = 20;
				const handleScroll = (event) => {
					if (event.key === 'ArrowDown') {
						event.preventDefault();
						scrollContainer.scrollBy({
							top: scrollIncrement,
							behavior: 'smooth'
						});
						scrollIncrement += 2;
					} else if (event.key === 'ArrowUp') {
						event.preventDefault();
						scrollContainer.scrollBy({
							top: -scrollIncrement,
							behavior: 'smooth'
						});
						scrollIncrement += 2;
					}
				};

				document.addEventListener('keydown', handleScroll);

				return () => {
					document.removeEventListener('keydown', handleScroll);
				};
			}, 0);
		}
	};

	return (
		<>
			<Editor
				handlePastedText={(text, html, editorState) => {
					if (text) {
						const contentState = editorState.getCurrentContent();
						const selectionState = editorState.getSelection();
						const newContentState = Modifier.insertText(contentState, selectionState, text);
						const newEditorState = EditorState.push(
							editorState,
							newContentState,
							'insert-characters'
						);

						setEditorState(newEditorState);
						return true; // Prevent default paste behavior
					}
					return false;
				}}
				ref={editorRef}
				onContentStateChange={handleContentStateChange}
				toolbarHidden={showFullToolbar ? false : true}
				toolbar={
					showFullToolbar
						? {
								options: [
									'inline',
									'fontSize',
									'fontFamily',
									'list',
									'textAlign',
									'colorPicker',
									'remove',
									'history'
								],
								inline: {
									options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace']
								},
								fontSize: {
									options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96]
								},
								fontFamily: {
									options: [
										'Arial',
										'Georgia',
										'Impact',
										'Tahoma',
										'Times New Roman',
										'Verdana',
										'Courier New',
										'Consolas',
										'Monaco'
									]
								},
								list: {
									options: ['unordered', 'ordered']
								},
								textAlign: {
									options: ['left', 'center', 'right', 'justify']
								},
								colorPicker: {
									colors: [
										'rgb(97,189,109)',
										'rgb(26,188,156)',
										'rgb(84,172,210)',
										'rgb(44,130,201)',
										'rgb(147,101,184)',
										'rgb(71,85,119)',
										'rgb(204,204,204)',
										'rgb(65,168,95)',
										'rgb(0,168,133)',
										'rgb(61,142,185)',
										'rgb(41,105,176)',
										'rgb(85,57,130)',
										'rgb(40,50,78)',
										'rgb(0,0,0)',
										'rgb(247,218,100)',
										'rgb(251,160,38)',
										'rgb(235,107,86)',
										'rgb(226,80,65)',
										'rgb(163,143,132)',
										'rgb(239,239,239)',
										'rgb(255,255,255)',
										'rgb(250,197,28)',
										'rgb(243,121,52)',
										'rgb(209,72,65)',
										'rgb(184,49,47)',
										'rgb(124,112,107)',
										'rgb(209,213,216)',
										'rgb(59, 130, 246)',
										'rgb(168, 85, 247)'
									]
								}
							}
						: undefined
				}
				editorState={editorState}
				onEditorStateChange={onEditorStateChange}
				toolbarClassName="editor-toolbar"
				wrapperClassName="editor-wrapper"
				editorClassName="editor-content"
				mention={{
					separator: ' ',
					trigger: '@',
					suggestions: suggestions
				}}
			/>
			{renderStyleSuggestions()}
		</>
	);
};

export default MyEditor;
